import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  CalendarIcon, 
  ClockIcon, 
  UserIcon,
  MagnifyingGlassIcon,
  ArrowRightIcon,
  PlayIcon
} from '@heroicons/react/24/outline'
import { useBlogPosts, formatFirestoreDate, getYouTubeEmbedUrl } from '../../../hooks/useFirestore'
import { orderBy, where } from 'firebase/firestore'
import styles from './Blog.module.css'

export default function Blog() {
  const { documents: posts, loading, error, getDocuments } = useBlogPosts()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTag, setSelectedTag] = useState('')

  useEffect(() => {
    // Solo obtener posts publicados, ordenados por fecha de publicación
    getDocuments([
      where('status', '==', 'published'),
      orderBy('publishedAt', 'desc')
    ])
  }, [])

  // Obtener todas las tags únicas
  const allTags = Array.from(
    new Set(posts.flatMap(post => post.tags || []))
  ).sort()

  // Filtrar posts
  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.author.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTag = !selectedTag || (post.tags && post.tags.includes(selectedTag))
    return matchesSearch && matchesTag
  })

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength).trim() + '...'
  }

  if (loading) {
    return (
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-8">Blog</h1>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6 text-red-400 max-w-md mx-auto">
              Error al cargar los posts: {error}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`${styles.blogContainer} pt-32 pb-20`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className={`${styles.blogHeader} text-center mb-16`}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-8">
            Blog
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-12">
            Insights, tendencias y conocimientos sobre tecnología e innovación
          </p>

          {/* Search and Filters */}
          <div className={styles.searchContainer}>
            <div className={styles.searchInputWrapper}>
              <MagnifyingGlassIcon className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Buscar artículos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
            </div>

            {allTags.length > 0 && (
              <div className={styles.tagsContainer}>
                <button
                  onClick={() => setSelectedTag('')}
                  className={`${styles.tagButton} ${
                    !selectedTag ? styles.tagButtonActive : styles.tagButtonInactive
                  }`}
                >
                  Todos
                </button>
                {allTags.map(tag => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`${styles.tagButton} ${
                      selectedTag === tag ? styles.tagButtonActive : styles.tagButtonInactive
                    }`}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </motion.div>

        {/* Posts Grid */}
        {filteredPosts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-16"
          >
            <div className="glass rounded-2xl p-12 max-w-md mx-auto">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-white mb-2">No hay artículos</h3>
              <p className="text-gray-400">
                {searchTerm || selectedTag 
                  ? 'No se encontraron artículos con los filtros seleccionados' 
                  : 'Pronto publicaremos contenido interesante'}
              </p>
            </div>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`${styles.postCard} glass rounded-2xl overflow-hidden`}
              >
                {/* Image */}
                {post.imageUrl && (
                  <div className={styles.postImageContainer}>
                    <img
                      src={post.imageUrl}
                      alt={post.title}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                )}

                <div className={styles.postContent}>
                  {/* Tags */}
                  {post.tags && post.tags.length > 0 && (
                    <div className={styles.tagContainer}>
                      {post.tags.slice(0, 3).map(tag => (
                        <span
                          key={tag}
                          className={styles.tag}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Type indicator */}
                  <div className="flex items-center space-x-2">
                    {post.type === 'video' && (
                      <div className={`${styles.postTypeIndicator} ${styles.videoIndicator}`}>
                        <PlayIcon className="w-3 h-3" />
                        <span>Video</span>
                      </div>
                    )}
                    {post.type === 'social_link' && (
                      <div className={`${styles.postTypeIndicator} ${styles.socialIndicator}`}>
                        <span>🔗</span>
                        <span>Link Social</span>
                      </div>
                    )}
                    {post.type === 'article' && (
                      <div className={`${styles.postTypeIndicator} ${styles.articleIndicator}`}>
                        <span>📄</span>
                        <span>Artículo</span>
                      </div>
                    )}
                  </div>

                  {/* Title */}
                  <div className={styles.postTitleArea}>
                    <h2 className={styles.postTitle}>
                      {post.title}
                    </h2>
                  </div>

                  {/* Main Content */}
                  <div className={styles.postMainContent}>
                    {/* Video embebido o Excerpt */}
                    {post.type === 'video' && post.videoUrl ? (
                      <div className={styles.videoEmbed}>
                        <iframe
                          src={getYouTubeEmbedUrl(post.videoUrl)}
                          title={post.title}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      </div>
                    ) : null}

                    <p className={styles.postExcerpt}>
                      {truncateText(post.excerpt, 150)}
                    </p>
                  </div>

                  {/* Footer with meta info and read more button */}
                  <div className={styles.postFooter}>
                    <div className={`${styles.metaInfo} mb-4`}>
                      <div className={styles.metaItem}>
                        <UserIcon className="w-4 h-4" />
                        <span>{post.author}</span>
                      </div>
                      <div className={styles.metaItem}>
                        <CalendarIcon className="w-4 h-4" />
                        <span>{formatFirestoreDate(post.publishedAt)}</span>
                      </div>
                      {post.readTime && (
                        <div className={styles.metaItem}>
                          <ClockIcon className="w-4 h-4" />
                          <span>{post.readTime} min</span>
                        </div>
                      )}
                    </div>

                    <button className={`${styles.readMoreButton} w-full inline-flex items-center justify-center space-x-2 font-medium py-3 px-4 rounded-lg border border-primary/20 hover:border-primary/40`}>
                      <span>Leer más</span>
                      <ArrowRightIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        {/* Pagination would go here if needed */}
      </div>
    </div>
  )
}
